import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Typography,
  Box,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import axios from '../api/axios';

const FeedbackDialog = ({ open, onClose }) => {
  const location = useLocation();
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!open) {
      setMessage('');
      setError('');
      setSent(false);
    }
  }, [open]);

  const submit = async () => {
    if (!message.trim()) {
      setError('Please write something before sending.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await axios.post('/api/feedback', {
        message: message.trim(),
        page: location.pathname + location.search,
      });
      setSent(true);
      setMessage('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send feedback.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Share feedback</DialogTitle>
      <DialogContent>
        {sent ? (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Thanks — got it.</Typography>
            <Typography variant="body2" color="text.secondary">
              Every note helps shape what gets built next.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Bug, idea, confusing UI — whatever it is, write it here and it lands in our inbox.
              We see which page you're on automatically.
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={5}
              maxRows={12}
              placeholder="What's on your mind?"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={submitting}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Page: {location.pathname}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {sent ? (
          <Button onClick={onClose} variant="contained">Done</Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button
              onClick={submit}
              variant="contained"
              startIcon={<SendIcon />}
              disabled={submitting || !message.trim()}
            >
              {submitting ? 'Sending…' : 'Send'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackDialog;
